import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const features = [
  { icon: 'ri-scissors-line', title: 'Bespoke Construction', desc: 'Every blazer is cut and sewn from scratch to your exact measurements and style preferences.' },
  { icon: 'ri-palette-line', title: 'Premium Fabrics', desc: 'Choose from fine Italian wools, linens, tweeds, and seasonal blends.' },
  { icon: 'ri-user-line', title: 'Personal Styling', desc: 'Work one-on-one with an expert clothier to design your ideal blazer.' },
  { icon: 'ri-settings-3-line', title: 'Custom Details', desc: 'Personalize lapels, buttons, lining, pockets, and monogram options.' },
  { icon: 'ri-home-line', title: 'House Calls Available', desc: 'We make house and office calls throughout Massachusetts and New England.' },
];

const styles = [
  { label: 'Sport Coats', img: 'https://public.readdy.ai/ai/img_res/8b316ae18e7b26b2829a84d3b227807a.jpg' },
  { label: 'Dinner Jackets', img: 'https://readdy.ai/api/search-image?query=custom%20dinner%20jacket%20blazer%20bespoke%20formal%20black%20velvet%20lapel%20premium%20menswear%20studio%20photography%20luxury%20fashion&width=400&height=500&seq=blz2&orientation=portrait' },
  { label: 'Casual Blazers', img: 'https://readdy.ai/api/search-image?query=custom%20casual%20blazer%20bespoke%20navy%20linen%20relaxed%20fit%20premium%20menswear%20studio%20photography%20modern%20style%20fashion&width=400&height=500&seq=blz3&orientation=portrait' },
];

const faqs = [
  { q: 'How long does a custom blazer take?', a: 'Custom blazers typically take 4–5 weeks from your initial consultation to final delivery.' },
  { q: 'What fabrics are available for blazers?', a: 'We carry Italian wools, linens, tweeds, cashmere blends, and seasonal fabrics in a wide range of colors and patterns.' },
  { q: 'Can I match my blazer to existing trousers?', a: 'Yes. Bring in your existing trousers and we will match the fabric weight, color, and construction for a cohesive look.' },
  { q: 'Do you offer house calls for blazer fittings?', a: 'Absolutely. We offer complimentary house and office calls throughout the Greater Boston area.' },
];

const CustomBlazersPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=luxury%20custom%20blazer%20tailor%20bespoke%20menswear%20premium%20fabric%20selection%20wellesley%20massachusetts%20elegant%20showroom%20warm%20lighting%20professional%20clothier&width=1400&height=700&seq=blzhero&orientation=landscape"
          alt="Custom Blazers"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/85 to-gray-900/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">Custom Clothiers — Wellesley, MA</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Custom Blazers<br />&amp; Sport Coats
          </h1>
          <p className="text-xl text-gray-200 max-w-xl mb-8">
            Handcrafted blazers tailored to your measurements, fabric choice, and personal style — for every occasion.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="bg-[#C4A962] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#B39952] transition-colors whitespace-nowrap">
              Book a Free Consultation
            </Link>
            <a href="tel:781-237-3166" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>781-237-3166
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose a Custom Blazer?</h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">A perfectly fitted blazer elevates every outfit — and ours are built to last a lifetime.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A962]/10 rounded-xl mb-5">
                  <i className={`${f.icon} text-[#C4A962] text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Styles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Blazers for Every Style</h2>
            <p className="text-lg text-gray-500">Sport coats, dinner jackets, and casual blazers — all made to measure.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {styles.map((s) => (
              <div key={s.label} className="relative rounded-2xl overflow-hidden group cursor-pointer">
                <img src={s.img} alt={s.label} className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <span className="text-white text-2xl font-bold">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">The Custom Blazer Process</h2>
            <p className="text-gray-400 text-lg">From first consultation to final fitting — a seamless experience.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Discuss your style goals, occasions, and fabric preferences with your clothier.' },
              { step: '02', title: 'Design', desc: 'Choose your fabric, lapel style, buttons, lining, and personalization details.' },
              { step: '03', title: 'Measurements', desc: 'Precise measurements are taken to create your unique pattern.' },
              { step: '04', title: 'Final Fitting', desc: 'Try on your finished blazer and receive any last adjustments.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="text-5xl font-bold text-[#C4A962]/30 mb-3">{s.step}</div>
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="border border-gray-200 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C4A962]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Design Your Custom Blazer Today</h2>
          <p className="text-white/80 text-lg mb-8">Visit our Wellesley showroom or request a house call — consultations are always complimentary.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="bg-white text-[#C4A962] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap">
              Book Free Consultation
            </Link>
            <a href="tel:781-237-3166" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-colors whitespace-nowrap">
              Call 781-237-3166
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomBlazersPage;
